import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, DIMENSION_META } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, MessageCircle, Lightbulb } from "lucide-react";

export default function ScenarioRun() {
  const { id } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [choices, setChoices] = useState({});
  const [debrief, setDebrief] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get(`/scenarios/${id}`).then((r) => setS(r.data)); }, [id]);

  if (!s) return <div className="p-16 text-center text-slate-500">Loading…</div>;

  const step = s.steps[stepIdx];
  const meta = DIMENSION_META[s.dimension];
  const chosen = choices[step?.id];

  const pick = (cid) => setChoices({ ...choices, [step.id]: cid });

  const nextStep = async () => {
    if (stepIdx < s.steps.length - 1) {
      setStepIdx(stepIdx + 1);
    } else {
      setLoading(true);
      try {
        const r = await api.post(`/scenarios/${id}/submit`, { choices });
        setDebrief(r.data.debrief);
      } catch { toast.error("Debrief failed"); }
      finally { setLoading(false); }
    }
  };

  if (debrief) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12 fade-up">
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Debrief</div>
        <h1 className="font-serif text-4xl text-[#0B1E36] mb-8">{s.title}</h1>
        <div className="bg-[#0B1E36] text-white p-10 rounded-2xl mb-6" data-testid="debrief-overall">
          <div className="flex items-center gap-2 text-[#C9A961] text-xs uppercase tracking-widest mb-4"><Sparkles className="h-4 w-4" /> Overall read</div>
          <p className="font-serif text-xl leading-relaxed">{debrief.overall}</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white p-8 rounded-2xl border border-slate-200" data-testid="debrief-strong">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-8 w-8 rounded-lg bg-[#2C7A7B]/15 grid place-items-center"><Lightbulb className="h-4 w-4 text-[#2C7A7B]" /></div>
              <div className="font-serif text-lg text-[#0B1E36]">Strong move</div>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">{debrief.strong_move}</p>
          </div>
          <div className="bg-white p-8 rounded-2xl border border-slate-200" data-testid="debrief-growth">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-8 w-8 rounded-lg bg-[#EC4899]/15 grid place-items-center"><MessageCircle className="h-4 w-4 text-[#EC4899]" /></div>
              <div className="font-serif text-lg text-[#0B1E36]">Try differently</div>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">{debrief.growth_move}</p>
          </div>
        </div>
        <div className="bg-[#C9A961]/10 border border-[#C9A961]/30 p-8 rounded-2xl mb-8" data-testid="debrief-reflection">
          <div className="text-xs uppercase tracking-widest text-[#6B5228] mb-2">Reflection prompt</div>
          <div className="font-serif text-xl text-[#0B1E36] leading-snug">{debrief.reflection_prompt}</div>
        </div>
        <div className="flex gap-3">
          <button onClick={() => nav("/app/scenarios")} data-testid="debrief-back" className="bg-[#0B1E36] text-white px-6 py-3 rounded-full font-semibold btn-lift hover:bg-[#132a4a]">
            Back to scenarios
          </button>
          <button onClick={() => { setDebrief(null); setChoices({}); setStepIdx(0); }} data-testid="debrief-retry" className="bg-white border border-slate-200 text-[#0B1E36] px-6 py-3 rounded-full font-semibold btn-lift hover:bg-slate-50">
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12 fade-up">
      <button onClick={() => nav("/app/scenarios")} data-testid="run-back" className="text-sm text-slate-500 hover:text-[#0B1E36] mb-6 inline-flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Back to scenarios
      </button>
      <div className="mb-6">
        <div className="flex items-center gap-3 text-xs mb-2">
          <span className="uppercase tracking-widest font-semibold" style={{ color: meta.color }}>{meta.label}</span>
          <span className="text-slate-300">•</span>
          <span className="text-slate-500">Step {stepIdx + 1} of {s.steps.length}</span>
        </div>
        <h1 className="font-serif text-3xl text-[#0B1E36]">{s.title}</h1>
      </div>

      <div className="bg-white p-8 rounded-2xl border border-slate-200 mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Context</div>
        <p className="text-slate-700 leading-relaxed">{s.context}</p>
      </div>

      <div className="bg-[#0B1E36] text-white p-8 rounded-2xl mb-6">
        <div className="text-xs uppercase tracking-widest text-[#C9A961] mb-3">Moment</div>
        <div className="font-serif text-2xl leading-snug" data-testid="scenario-prompt">{step.prompt}</div>
      </div>

      <div className="space-y-3 mb-8">
        {step.choices.map((c) => {
          const active = chosen === c.id;
          return (
            <button
              key={c.id} onClick={() => pick(c.id)} data-testid={`choice-${c.id}`}
              className={`w-full text-left p-5 rounded-xl border-2 transition-colors ${
                active ? "border-[#EC4899] bg-[#EC4899]/5" : "border-slate-200 hover:border-slate-300 bg-white"
              }`}>
              <div className="flex items-start gap-3">
                <div className={`h-6 w-6 rounded-full border-2 grid place-items-center shrink-0 mt-0.5 ${
                  active ? "border-[#EC4899] bg-[#EC4899]" : "border-slate-300"
                }`}>
                  {active && <div className="h-2 w-2 bg-white rounded-full" />}
                </div>
                <div className="font-medium text-[#0B1E36] leading-relaxed">{c.label}</div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex justify-end">
        <button
          disabled={!chosen || loading} onClick={nextStep} data-testid="scenario-next"
          className="bg-[#EC4899] text-white px-8 py-3 rounded-full font-semibold btn-lift disabled:opacity-50 hover:bg-[#DB2777] shadow-[0_8px_24px_rgba(236,72,153,0.25)]">
          {loading ? "Generating debrief…" : (stepIdx === s.steps.length - 1 ? "Finish & get debrief" : "Continue")}
        </button>
      </div>
    </div>
  );
}
